import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type PersonalizeMessageT = {
  id: string
  role: 'user' | 'assistant'
  content: string
  quickReplies?: string[]
  chapterIndex: number
  createdAt: number
}

export type ChapterProgressT = {
  momentId: string
  collected: number
  completed: boolean
}

export type PendingIntentT = 'skip' | null

// Anchors per chapter — flat, not scaled by chapter count. An anchor is one
// concrete memory (who/where + when/what + why it stuck); depth is the book's
// job, so this interview only indexes the user's life.
export const ANCHORS_PER_CHAPTER = 2

// Hard cap on questions asked in one chapter. A cap, not a target: chapters
// with forthcoming users finish well under it. Tightened for large selections
// so an 8-chapter interview stays inside the ~30-question envelope.
export const getQuestionBudget = (momentCount: number) => (momentCount <= 5 ? 5 : 4)

type T = {
  selectedMomentIds: string[]
  chapters: ChapterProgressT[]
  storiesTarget: number
  questionBudget: number
  questionsInChapter: number
  questionsOnAnchor: number
  currentChapterIndex: number
  // True between a backstop force-closing a chapter and the assistant actually
  // announcing the new one. See applyChatResult.
  awaitingChapterAnnouncement: boolean
  messages: PersonalizeMessageT[]
  isComplete: boolean
  pendingIntent: PendingIntentT

  startSession: (momentIds: string[]) => void
  addMessage: (message: Omit<PersonalizeMessageT, 'id' | 'createdAt'>) => void
  applyChatResult: (result: { storyCollected: boolean; chapterComplete: boolean; forChapterIndex: number }) => void
  skipCurrentQuestion: () => void
  clearPendingIntent: () => void
  reset: () => void
}

const initialState = {
  selectedMomentIds: [] as string[],
  chapters: [] as ChapterProgressT[],
  storiesTarget: 0,
  questionBudget: 0,
  questionsInChapter: 0,
  questionsOnAnchor: 0,
  currentChapterIndex: 0,
  awaitingChapterAnnouncement: false,
  messages: [] as PersonalizeMessageT[],
  isComplete: false,
  pendingIntent: null as PendingIntentT,
}

export const useStorePersonalize = create<T>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Keeps an in-flight conversation when the user revisits the selection
      // page without changing anything; any change restarts the chat.
      startSession: (momentIds) => {
        const state = get()
        const sameSelection =
          momentIds.length === state.selectedMomentIds.length &&
          momentIds.every((id, i) => state.selectedMomentIds[i] === id)
        if (sameSelection && state.messages.length > 0) return

        set({
          ...initialState,
          selectedMomentIds: momentIds,
          storiesTarget: ANCHORS_PER_CHAPTER,
          questionBudget: getQuestionBudget(momentIds.length),
          chapters: momentIds.map((momentId) => ({
            momentId,
            collected: 0,
            completed: false,
          })),
        })
      },

      addMessage: (message) =>
        set((state) => ({
          messages: [
            ...state.messages,
            {
              ...message,
              id: `${Date.now()}-${state.messages.length}`,
              createdAt: Date.now(),
            },
          ],
          // Every assistant turn carries exactly one question, so the message
          // itself is the counter — no extra plumbing from the API response.
          // applyChatResult (which resets these) always runs first, so a
          // question opening a fresh anchor or chapter counts as its first.
          questionsInChapter: message.role === 'assistant' ? state.questionsInChapter + 1 : state.questionsInChapter,
          questionsOnAnchor: message.role === 'assistant' ? state.questionsOnAnchor + 1 : state.questionsOnAnchor,
        })),

      applyChatResult: ({ storyCollected, chapterComplete, forChapterIndex }) =>
        set((state) => {
          if (state.isComplete) return state
          if (forChapterIndex !== state.currentChapterIndex) return state

          // A backstop below can close a chapter on a turn where the assistant
          // said nothing about it — it only learns from the NEXT turn's state.
          // So the message we are scoring here is the one that finally announces
          // the new chapter, and the answer it reacts to still belongs to the
          // OLD chapter, which is already closed at full count. Crediting it to
          // the new chapter is what made the tab strip read "1 of 2" against a
          // freshly printed "Stories collected: 0 of 2" in the same breath.
          // Drop the score and let the new chapter start where it says it does.
          if (state.awaitingChapterAnnouncement) {
            return {
              awaitingChapterAnnouncement: false,
              // The announcing message carries this chapter's first question,
              // so its budget starts here too (addMessage counts it next).
              questionsInChapter: 0,
              questionsOnAnchor: 0,
            }
          }

          // The question budget is a hard gate too, so it has to be evaluated
          // even on a turn the model scored as nothing — that is exactly the
          // one-word-answer run the budget exists to terminate.
          const budgetSpent = state.questionBudget > 0 && state.questionsInChapter >= state.questionBudget
          if (!storyCollected && !chapterComplete && !budgetSpent) return state

          const current = state.chapters[state.currentChapterIndex]
          const closes = chapterComplete || budgetSpent
          const collected = closes
            ? state.storiesTarget
            : Math.min(current.collected + (storyCollected ? 1 : 0), state.storiesTarget)
          // Backstop: the counter is the hard gate on progression, so a chapter
          // that filled its quota advances even if the model forgot to send
          // chapterComplete. Without this the count pins at X of X, the tab strip
          // never moves on and the interview cannot finish. The prompt asks for
          // chapterComplete on the boundary turn, so this should rarely fire.
          const isDone = current.completed || closes || collected >= state.storiesTarget

          const chapters = state.chapters.map((chapter, i) =>
            i === state.currentChapterIndex ? { ...chapter, collected, completed: isDone } : chapter,
          )

          const isLastChapter = state.currentChapterIndex >= state.chapters.length - 1
          const advances = isDone && !isLastChapter
          return {
            chapters,
            currentChapterIndex: advances ? state.currentChapterIndex + 1 : state.currentChapterIndex,
            isComplete: state.isComplete || (isDone && isLastChapter),
            // Set only when a backstop advanced the chapter behind the
            // assistant's back; when it sent chapterComplete itself, this very
            // message already announces the new chapter.
            awaitingChapterAnnouncement: advances && !chapterComplete,
            // A new chapter starts both budgets over; a scored anchor only
            // starts the per-anchor one over.
            questionsInChapter: advances ? 0 : state.questionsInChapter,
            questionsOnAnchor: advances || storyCollected ? 0 : state.questionsOnAnchor,
          }
        }),

      skipCurrentQuestion: () =>
        set((state) => {
          if (state.isComplete) return state
          return { pendingIntent: 'skip' as PendingIntentT }
        }),

      // A skip opens a different memory, so the per-anchor budget starts over —
      // but it still costs the user a turn, so the chapter budget is untouched.
      // A skip turn already asks from the new chapter's brief, so it settles any
      // pending announcement: the next answer really does belong to that chapter.
      clearPendingIntent: () => set({ pendingIntent: null, questionsOnAnchor: 0, awaitingChapterAnnouncement: false }),

      reset: () => set({ ...initialState }),
    }),
    {
      name: 'mw-personalize-chapters',
      storage: createJSONStorage(() => sessionStorage),
      version: 3,
      // An interview already in flight in sessionStorage must survive a deploy,
      // so v3 seeds the three budget fields rather than leaving them undefined
      // (an undefined questionBudget would clamp server-side and NaN the gate).
      migrate: (persistedState) => {
        const state = persistedState as T
        return {
          ...state,
          // A v2 session carries the old 3-8 target; adopting the new one mid
          // interview is the point of the change, so clamp the counts down to it
          // rather than letting a half-finished chapter keep the long budget.
          storiesTarget: ANCHORS_PER_CHAPTER,
          questionBudget: getQuestionBudget(state.selectedMomentIds?.length ?? 0),
          questionsInChapter: state.questionsInChapter ?? 0,
          questionsOnAnchor: state.questionsOnAnchor ?? 0,
          awaitingChapterAnnouncement: state.awaitingChapterAnnouncement === true,
          pendingIntent: state.pendingIntent === 'skip' ? 'skip' : null,
          chapters: (state.chapters ?? []).map(({ momentId, collected, completed }) => ({
            momentId,
            collected: Math.min(collected ?? 0, ANCHORS_PER_CHAPTER),
            completed,
          })),
        }
      },
    },
  ),
)

export const getVisibleChapterIndex = (state: { currentChapterIndex: number; awaitingChapterAnnouncement: boolean }) =>
  state.awaitingChapterAnnouncement ? Math.max(0, state.currentChapterIndex - 1) : state.currentChapterIndex
